import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ONBOARDING_TOUR_MODULES,
  TOUR_EXPAND_MENU_EVENT,
  getTourModuleById,
} from "../config/onboardingTourConfig";
import { markTourModuleVisited, markTourStepVisited } from "../utils/onboardingTourProgress";

const OnboardingTourContext = createContext(null);

function waitForElement(selector, timeout = 6000) {
  if (!selector) return Promise.resolve(null);

  return new Promise((resolve) => {
    const existing = document.querySelector(selector);
    if (existing) {
      resolve(existing);
      return;
    }

    const observer = new MutationObserver(() => {
      const found = document.querySelector(selector);
      if (found) {
        observer.disconnect();
        resolve(found);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => {
      observer.disconnect();
      resolve(document.querySelector(selector));
    }, timeout);
  });
}

function getTargetRect(target) {
  if (!target) return null;
  const rect = target.getBoundingClientRect();
  const pad = 8;
  return {
    top: rect.top - pad,
    left: rect.left - pad,
    width: rect.width + pad * 2,
    height: rect.height + pad * 2,
  };
}

export function OnboardingTourProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeModuleId, setActiveModuleId] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const activeModule = useMemo(
    () => (activeModuleId ? getTourModuleById(activeModuleId) : null),
    [activeModuleId]
  );

  const activeStep = activeModule?.steps?.[stepIndex] || null;
  const isActive = Boolean(activeModule && activeStep);

  const updateTargetRect = useCallback(async (step) => {
    if (!step?.target) {
      setTargetRect(null);
      return;
    }
    const el = await waitForElement(step.target);
    setTargetRect(getTargetRect(el));
  }, []);

  const goToStep = useCallback(
    async (module, index) => {
      const step = module?.steps?.[index];
      if (!step) return;

      setIsTransitioning(true);
      setStepIndex(index);

      if (step.expandMenuKey) {
        window.dispatchEvent(
          new CustomEvent(TOUR_EXPAND_MENU_EVENT, {
            detail: { menuKey: step.expandMenuKey },
          })
        );
      }

      const normalizedRoute = step.route.startsWith("/") ? step.route : `/${step.route}`;
      if (location.pathname !== normalizedRoute) {
        navigate(normalizedRoute);
      }

      await new Promise((resolve) => setTimeout(resolve, 320));
      await updateTargetRect(step);
      setIsTransitioning(false);
    },
    [location.pathname, navigate, updateTargetRect]
  );

  const startModule = useCallback(
    async (moduleId) => {
      const module = getTourModuleById(moduleId);
      if (!module?.steps?.length) return;
      setActiveModuleId(moduleId);
      await goToStep(module, 0);
    },
    [goToStep]
  );

  const stopTour = useCallback(() => {
    setActiveModuleId(null);
    setStepIndex(0);
    setTargetRect(null);
    setIsTransitioning(false);
  }, []);

  const nextStep = useCallback(async () => {
    if (!activeModule || !activeStep) return;

    markTourStepVisited(activeStep.id);
    window.dispatchEvent(new CustomEvent("onboarding-tour:progress"));

    const isLast = stepIndex >= activeModule.steps.length - 1;
    if (isLast) {
      markTourModuleVisited(activeModule);
      stopTour();
      window.dispatchEvent(new CustomEvent("onboarding-tour:progress"));
      return;
    }

    await goToStep(activeModule, stepIndex + 1);
  }, [activeModule, activeStep, goToStep, stepIndex, stopTour]);

  const prevStep = useCallback(async () => {
    if (!activeModule || stepIndex <= 0) return;
    await goToStep(activeModule, stepIndex - 1);
  }, [activeModule, goToStep, stepIndex]);

  const skipModule = useCallback(() => {
    if (activeModule) {
      markTourModuleVisited(activeModule);
      window.dispatchEvent(new CustomEvent("onboarding-tour:progress"));
    }
    stopTour();
  }, [activeModule, stopTour]);

  useEffect(() => {
    if (!isActive || !activeStep) return undefined;

    const handleLayoutChange = () => {
      void updateTargetRect(activeStep);
    };

    window.addEventListener("resize", handleLayoutChange);
    window.addEventListener("scroll", handleLayoutChange, true);
    return () => {
      window.removeEventListener("resize", handleLayoutChange);
      window.removeEventListener("scroll", handleLayoutChange, true);
    };
  }, [activeStep, isActive, updateTargetRect]);

  useEffect(() => {
    if (!isActive || !activeStep) return;
    void updateTargetRect(activeStep);
  }, [location.pathname, isActive, activeStep, updateTargetRect]);

  const value = useMemo(
    () => ({
      isActive,
      isTransitioning,
      activeModuleId,
      activeModule,
      activeStep,
      stepIndex,
      totalSteps: activeModule?.steps?.length || 0,
      targetRect,
      startModule,
      nextStep,
      prevStep,
      skipModule,
      stopTour,
    }),
    [
      isActive,
      isTransitioning,
      activeModuleId,
      activeModule,
      activeStep,
      stepIndex,
      targetRect,
      startModule,
      nextStep,
      prevStep,
      skipModule,
      stopTour,
    ]
  );

  return (
    <OnboardingTourContext.Provider value={value}>{children}</OnboardingTourContext.Provider>
  );
}

export function useOnboardingTour() {
  const context = useContext(OnboardingTourContext);
  if (!context) {
    throw new Error("useOnboardingTour must be used within OnboardingTourProvider");
  }
  return context;
}

export function useOnboardingTourOptional() {
  return useContext(OnboardingTourContext);
}
